import { InvoiceEntry, PaymentStatus } from '@dataTypes/billing.types'
import { addPaymentEntry } from '@store/actions/billing.action'
import { AppDispatch, RootState } from '@store/index'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { exportLedgerEntriesCsv } from '@services/api/billing/ledger.api'
import { exportInvoiceEntriesCsv } from '@services/api/billing/invoice.api'
import { exportPaymentEntriesCsv } from '@services/api/billing/payment.api'
import { exportTransactionEntriesCsv } from '@services/api/billing/transaction.api'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  buildInvoiceFilters,
  buildLedgerFilters,
  buildPaymentFilters,
  buildProviderNameToIdsMap,
  buildTransactionFilters,
  getSelectedProviderIds,
} from './filterBuilders'
import { useProvidersForBilling } from './useProvidersForBilling'

export enum BillingTabType {
  Ledger = 'ledger',
  Invoices = 'invoices',
  Payments = 'payments',
  Transactions = 'transactions',
}

interface UseBillingOptions {
  activeTab: BillingTabType
  onChangeTab: (tab: BillingTabType) => void
}

type TabFilterConfig = {
  filters: Record<string, string[]>
  labels: Record<string, string>
}

const BILLING_SEARCH_DEBOUNCE_MS = 300

interface UseBillingHooks {
  activeTab: BillingTabType
  searchQuery: string
  /** `searchQuery` after debounce; use for transaction API search and CSV export. */
  debouncedSearchQuery: string
  filters: Record<string, string[]>
  showCreateInvoiceModal: boolean
  showAddInvoiceModal: boolean
  tabFilterConfigs: Record<
    Exclude<BillingTabType, BillingTabType.Ledger>,
    TabFilterConfig
  >
  handleChangeTab: (tab: BillingTabType) => void
  handleFilterChange: (newFilters: Record<string, string[]>) => void
  handleSearchChange: (value: string) => void
  handleCreateInvoice: () => void
  handleAddInvoice: () => void
  handleCloseCreateInvoiceModal: () => void
  handleCloseAddInvoiceModal: () => void
  handleAddFromInvoice: (invoice: InvoiceEntry) => void
  handleExport: () => void
  setShowCreateInvoiceModal: (show: boolean) => void
}

const BILLING_LOADING_CONTAINER = '.billing-page'

const useBilling = (options: UseBillingOptions): UseBillingHooks => {
  const { activeTab, onChangeTab } = options
  const dispatch = useDispatch<AppDispatch>()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const invoiceListSort = useSelector(
    (state: RootState) => state.billing.invoicePagination.sort,
  )
  const ledgerListSort = useSelector(
    (state: RootState) => state.billing.ledgerPagination.sort,
  )
  const paymentListSort = useSelector(
    (state: RootState) => state.billing.paymentPagination.sort,
  )
  const transactionListSort = useSelector(
    (state: RootState) => state.billing.transactionPagination.sort,
  )
  const selectedPatientId = useSelector(
    (state: RootState) => state.patient.selectedPatient?.patient_id,
  )
  const reduxClinicId = useSelector(
    (state: RootState) => state.clinic.selectedClinic?.clinic_id,
  )
  const selectedClinicId = useMemo(
    () => reduxClinicId || localStorage.getItem('clinic_id') || undefined,
    [reduxClinicId],
  )

  // Local UI state: modal visibility and form/tab state (not in Redux per architecture)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    BILLING_SEARCH_DEBOUNCE_MS,
  )
  const [filters, setFilters] = useState<Record<string, string[]>>({})
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false)
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false)
  const providers = useProvidersForBilling()

  const providerNameToIdsMap = useMemo(
    () => buildProviderNameToIdsMap(providers),
    [providers],
  )
  const selectedProviderIds = useMemo(
    () => getSelectedProviderIds(filters.providerName, providerNameToIdsMap),
    [filters.providerName, providerNameToIdsMap],
  )

  const handleAddFromInvoice = useCallback(
    (invoice: InvoiceEntry): void => {
      dispatch(addPaymentEntry(invoice))
        .unwrap()
        .then(() => {
          onChangeTab(BillingTabType.Payments)
        })
        .catch((error) => {
          // If duplicate, still switch to payments tab so user can see it's already selected
          if (error === 'Invoice is already selected for payment') {
            onChangeTab(BillingTabType.Payments)
          }
        })
    },
    [dispatch, onChangeTab],
  )

  const handleChangeTab = useCallback(
    (tab: BillingTabType): void => {
      onChangeTab(tab)
    },
    [onChangeTab],
  )

  const handleFilterChange = useCallback(
    (newFilters: Record<string, string[]>): void => {
      setFilters(newFilters)
    },
    [],
  )

  const handleSearchChange = useCallback((value: string): void => {
    setSearchQuery(value)
  }, [])

  const handleCreateInvoice = useCallback((): void => {
    onChangeTab(BillingTabType.Invoices)
    requestAnimationFrame(() => {
      setShowCreateInvoiceModal(true)
    })
  }, [onChangeTab])

  const handleOpenAddInvoice = useCallback((): void => {
    setShowAddInvoiceModal(true)
  }, [])

  const handleCloseCreateInvoiceModal = useCallback((): void => {
    setShowCreateInvoiceModal(false)
  }, [])

  const handleCloseAddInvoiceModal = useCallback((): void => {
    setShowAddInvoiceModal(false)
  }, [])

  const handleExport = useCallback(async (): Promise<void> => {
    showLoading({ container: BILLING_LOADING_CONTAINER })
    let filename: string
    let successMessage: string
    let failMessage: string
    let blob: Blob
    failMessage = 'Export failed.'
    try {
      switch (activeTab) {
        case BillingTabType.Ledger: {
          const exportFilters = buildLedgerFilters({
            search: debouncedSearchQuery,
            selectedProviderIds,
            sort: ledgerListSort,
            selectedPatientId,
            selectedClinicId,
          })
          blob = await exportLedgerEntriesCsv(exportFilters)
          filename = 'ledger-entries.csv'
          successMessage = 'Ledger entries have been exported to CSV.'
          failMessage = 'Failed to export ledger entries.'
          break
        }
        case BillingTabType.Invoices: {
          const exportFilters = buildInvoiceFilters({
            search: debouncedSearchQuery,
            tabFilters: filters,
            sort: invoiceListSort,
            selectedPatientId,
            selectedClinicId,
          })
          blob = await exportInvoiceEntriesCsv(exportFilters)
          filename = 'invoice-entries.csv'
          successMessage = 'Invoice entries have been exported to CSV.'
          failMessage = 'Failed to export invoice entries.'
          break
        }
        case BillingTabType.Payments: {
          const exportFilters = buildPaymentFilters({
            search: debouncedSearchQuery,
            tabFilters: filters,
            sort: paymentListSort,
            selectedPatientId,
            selectedClinicId,
          })
          blob = await exportPaymentEntriesCsv(exportFilters)
          filename = 'payment-entries.csv'
          successMessage = 'Payment entries have been exported to CSV.'
          failMessage = 'Failed to export payment entries.'
          break
        }
        case BillingTabType.Transactions: {
          const exportFilters = buildTransactionFilters({
            search: debouncedSearchQuery,
            tabFilters: filters,
            sort: transactionListSort,
            selectedPatientId,
            selectedClinicId,
          })
          blob = await exportTransactionEntriesCsv(exportFilters)
          filename = 'transaction-entries.csv'
          successMessage = 'Transaction entries have been exported to CSV.'
          failMessage = 'Failed to export transaction entries.'
          break
        }
        default:
          hideLoading(BILLING_LOADING_CONTAINER)
          return
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      showToast(createSuccessToast('Export complete', successMessage))
    } catch (err) {
      const message = err instanceof Error ? err.message : failMessage
      showToast(createFailedToast('Export failed', message))
    } finally {
      hideLoading(BILLING_LOADING_CONTAINER)
    }
  }, [
    activeTab,
    filters,
    debouncedSearchQuery,
    selectedProviderIds,
    selectedPatientId,
    selectedClinicId,
    ledgerListSort,
    invoiceListSort,
    paymentListSort,
    transactionListSort,
    showLoading,
    hideLoading,
    showToast,
  ])

  const tabFilterConfigs = useMemo(
    (): Record<
      Exclude<BillingTabType, BillingTabType.Ledger>,
      TabFilterConfig
    > => ({
      [BillingTabType.Invoices]: {
        filters: {
          status: [
            PaymentStatus.Unpaid,
            PaymentStatus.PartiallyPaid,
            PaymentStatus.Paid,
          ],
        },
        labels: {
          status: 'Status',
        },
      },
      [BillingTabType.Payments]: {
        filters: {
          method: ['Cash', 'Card', 'Insurance', 'Bank Transfer'],
        },
        labels: {
          method: 'Payment Method',
        },
      },
      [BillingTabType.Transactions]: {
        filters: {
          transaction_type: ['Billing', 'Claim', 'Procedure'],
        },
        labels: {
          transaction_type: 'Transaction Type',
        },
      },
    }),
    [],
  )

  return {
    activeTab,
    searchQuery,
    debouncedSearchQuery,
    filters,
    showCreateInvoiceModal,
    showAddInvoiceModal,
    tabFilterConfigs,
    handleChangeTab,
    handleFilterChange,
    handleSearchChange,
    handleCreateInvoice,
    handleAddInvoice: handleOpenAddInvoice,
    handleCloseCreateInvoiceModal,
    handleCloseAddInvoiceModal,
    handleAddFromInvoice,
    handleExport,
    setShowCreateInvoiceModal,
  }
}

export default useBilling
